/**
 * Signal processing primitives. Deliberately free of browser APIs so they can
 * be tested against synthetic signals in Node.
 */

/** Root-mean-square level of a buffer. */
export function rms(buffer: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < buffer.length; i++) {
    const v = buffer[i]!;
    sum += v * v;
  }
  return Math.sqrt(sum / Math.max(1, buffer.length));
}

export function dbfs(amplitude: number): number {
  return 20 * Math.log10(Math.max(amplitude, 1e-12));
}

/** Wrap an angle into (-pi, pi]. */
export function wrapToPi(angle: number): number {
  let a = angle;
  while (a <= -Math.PI) a += 2 * Math.PI;
  while (a > Math.PI) a -= 2 * Math.PI;
  return a;
}

export interface BinResult {
  readonly phase: number;
  readonly magnitude: number;
}

/**
 * Single-bin DFT of a Hann-windowed segment, evaluated at an arbitrary (not
 * necessarily bin-aligned) frequency.
 *
 * The complex exponential is generated by repeated rotation rather than by
 * calling trig per sample, with periodic renormalisation to stop the rotor
 * drifting off the unit circle over long windows.
 *
 * The returned phase is referenced to the first sample of the segment.
 */
export function analyseBin(
  buffer: Float32Array,
  offset: number,
  length: number,
  frequency: number,
  sampleRate: number,
): BinResult {
  const w = (2 * Math.PI * frequency) / sampleRate;
  const cosW = Math.cos(w);
  const sinW = Math.sin(w);

  // Rotor for exp(-j*w*i), advanced one step per sample.
  let rotRe = 1;
  let rotIm = 0;

  // Hann window, also generated by rotation: 0.5 - 0.5*cos(2*pi*i/(N-1)).
  const hw = (2 * Math.PI) / Math.max(1, length - 1);
  const cosH = Math.cos(hw);
  const sinH = Math.sin(hw);
  let hanRe = 1;
  let hanIm = 0;

  let re = 0;
  let im = 0;

  for (let i = 0; i < length; i++) {
    const sample = buffer[offset + i]!;
    const window = 0.5 - 0.5 * hanRe;
    const s = sample * window;

    re += s * rotRe;
    im += s * rotIm;

    // rot *= exp(-j*w)  =>  (re + j*im)(cosW - j*sinW)
    const nextRotRe = rotRe * cosW + rotIm * sinW;
    const nextRotIm = rotIm * cosW - rotRe * sinW;
    rotRe = nextRotRe;
    rotIm = nextRotIm;

    const nextHanRe = hanRe * cosH - hanIm * sinH;
    const nextHanIm = hanIm * cosH + hanRe * sinH;
    hanRe = nextHanRe;
    hanIm = nextHanIm;

    if ((i & 1023) === 1023) {
      const rn = Math.hypot(rotRe, rotIm) || 1;
      rotRe /= rn;
      rotIm /= rn;
      const hn = Math.hypot(hanRe, hanIm) || 1;
      hanRe /= hn;
      hanIm /= hn;
    }
  }

  // Hann sums to length/2, and a real cosine splits its energy between the
  // positive and negative bins; normalise so magnitude reads as amplitude.
  const scale = 4 / Math.max(1, length);
  return { phase: Math.atan2(im, re), magnitude: Math.hypot(re, im) * scale };
}

/**
 * Refine a coarse frequency estimate by measuring how far the signal's phase
 * drifts from the estimate across a known time separation.
 *
 * For a steady tone this is far more precise than the autocorrelation estimate
 * that seeds it: the longer the separation, the finer the resolution. But a
 * long separation also narrows the range over which the phase difference can be
 * unwrapped without ambiguity — the estimate must already be within
 * +/- 1/(2*dt) Hz. So the separation is grown in stages, each stage tightening
 * the estimate enough for the next to stay unambiguous.
 *
 * Returns the input unchanged if the buffer is too short to stage properly.
 */
export function refineFrequency(
  buffer: Float32Array,
  sampleRate: number,
  coarseHz: number,
  stages: readonly number[] = [0.125, 0.25, 0.5],
): number {
  const n = buffer.length;
  if (!(coarseHz > 0) || n < 256) return coarseHz;

  let estimate = coarseHz;
  for (const fraction of stages) {
    const lag = Math.floor(n * fraction);
    const segment = n - lag;
    // Need at least a couple of cycles in each segment for a meaningful phase.
    if (lag < 1 || segment < 64 || segment < (2 * sampleRate) / estimate) continue;

    const a = analyseBin(buffer, 0, segment, estimate, sampleRate);
    const b = analyseBin(buffer, lag, segment, estimate, sampleRate);

    const dt = lag / sampleRate;
    // How far the phase actually advanced, minus how far the current estimate
    // says it should have. Wrapping resolves it to the smallest explanation,
    // which is the right one only while the estimate is within +/- 1/(2*dt).
    const expected = 2 * Math.PI * estimate * dt;
    const residual = wrapToPi(b.phase - a.phase - expected);

    const next = estimate + residual / (2 * Math.PI * dt);
    if (!Number.isFinite(next) || next <= 0) return estimate;
    estimate = next;
  }
  return estimate;
}

/**
 * Guard against the octave errors that autocorrelation makes on harmonically
 * rich tones by checking whether real energy sits an octave below the estimate.
 *
 * Only halves the estimate when the sub-octave is comparably strong, since many
 * organ stops legitimately have a weak fundamental.
 */
export function correctSubOctave(
  buffer: Float32Array,
  sampleRate: number,
  frequency: number,
  minHz: number,
  threshold = 0.55,
): number {
  const half = frequency / 2;
  if (half < minHz) return frequency;
  const length = buffer.length;
  if (length < (4 * sampleRate) / half) return frequency;

  const here = analyseBin(buffer, 0, length, frequency, sampleRate).magnitude;
  const below = analyseBin(buffer, 0, length, half, sampleRate).magnitude;
  return below > here * threshold ? half : frequency;
}

/**
 * Amplitude envelope of the signal within a narrow band around `centreHz`,
 * sampled at `envelopeRate` Hz.
 *
 * Each output point is the magnitude of a Hann-windowed bin at `centreHz`,
 * taken over a window of two hops with 50% overlap. The magnitude is invariant
 * to where the window starts, so each block can restart its own rotor.
 *
 * A plain boxcar average would be cheaper but its sidelobes are poor: the
 * image of the carrier lands about two octaves up and leaks straight back into
 * the envelope as ripple, which the beat detector then happily reports as a
 * beat. The Hann window puts that leakage far below the real modulation.
 */
export function bandEnvelope(
  buffer: Float32Array,
  sampleRate: number,
  centreHz: number,
  envelopeRate: number,
): Float32Array {
  const hop = Math.max(1, Math.round(sampleRate / envelopeRate));
  const window = hop * 2;
  const count = Math.floor((buffer.length - window) / hop) + 1;
  if (count <= 0) return new Float32Array(0);

  const out = new Float32Array(count);
  for (let b = 0; b < count; b++) {
    out[b] = analyseBin(buffer, b * hop, window, centreHz, sampleRate).magnitude;
  }
  return out;
}

export interface BeatEstimate {
  readonly hz: number;
  readonly confidence: number;
  /** Peak-to-mean modulation depth, 0 for a perfectly steady envelope. */
  readonly depth: number;
}

/**
 * Find the dominant modulation rate of an envelope, in Hz, by normalised
 * autocorrelation.
 *
 * Two details matter for getting the right answer rather than a plausible one:
 *
 * - Lags are capped at half the envelope length, so every correlation is taken
 *   over at least half the capture. Short overlaps correlate well by accident.
 *   This is what bounds the slowest measurable beat: you cannot read a 0.3 Hz
 *   beat out of a two-second capture, and the function will say so by
 *   returning null rather than guessing.
 * - The *first* strong peak is taken, not the tallest. A periodic envelope
 *   correlates just as well at two, three or fifteen periods, and picking the
 *   global maximum reports a sub-multiple of the true rate.
 *
 * Returns null when the envelope is too flat to contain a convincing
 * modulation — which is exactly what a well-tuned unison looks like.
 */
export function envelopePeriodHz(
  envelope: Float32Array,
  envelopeRate: number,
  minHz: number,
  maxHz: number,
  minConfidence = 0.3,
  minDepth = 0.02,
): BeatEstimate | null {
  const n = envelope.length;
  if (n < 16) return null;

  let mean = 0;
  for (let i = 0; i < n; i++) mean += envelope[i]!;
  mean /= n;
  if (!(mean > 0)) return null;

  const centred = new Float32Array(n);
  let energy = 0;
  for (let i = 0; i < n; i++) {
    const v = envelope[i]! - mean;
    centred[i] = v;
    energy += v * v;
  }
  if (energy <= 0) return null;

  const depth = Math.sqrt(energy / n) / mean;
  if (depth < minDepth) return null;

  const minLag = Math.max(1, Math.floor(envelopeRate / maxHz));
  const maxLag = Math.min(Math.floor(n / 2), Math.ceil(envelopeRate / minHz));
  if (maxLag <= minLag + 1) return null;

  const scores = new Float64Array(maxLag + 2);
  let peakScore = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let sum = 0;
    let normA = 0;
    let normB = 0;
    const span = n - lag;
    for (let i = 0; i < span; i++) {
      const a = centred[i]!;
      const b = centred[i + lag]!;
      sum += a * b;
      normA += a * a;
      normB += b * b;
    }
    const denom = Math.sqrt(normA * normB);
    const score = denom > 0 ? sum / denom : 0;
    scores[lag] = score;
    if (score > peakScore) peakScore = score;
  }

  if (peakScore < minConfidence) return null;
  const threshold = peakScore * 0.9;

  let chosen = -1;
  for (let lag = minLag + 1; lag < maxLag; lag++) {
    const here = scores[lag]!;
    if (here >= threshold && here > scores[lag - 1]! && here >= scores[lag + 1]!) {
      chosen = lag;
      break;
    }
  }
  if (chosen < 0) return null;

  // Parabolic interpolation through the peak, for resolution finer than one
  // envelope sample — a beat rate is worth reading to a tenth of a hertz.
  const left = scores[chosen - 1]!;
  const centre = scores[chosen]!;
  const right = scores[chosen + 1]!;
  const curvature = left - 2 * centre + right;
  const shift = curvature !== 0 ? (0.5 * (left - right)) / curvature : 0;
  const lag = chosen + (Math.abs(shift) <= 1 ? shift : 0);

  if (!(lag > 0)) return null;
  return { hz: envelopeRate / lag, confidence: centre, depth };
}

/** Median of a numeric list. Does not mutate the input. */
export function median(values: readonly number[]): number {
  if (values.length === 0) return NaN;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = sorted.length >> 1;
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}
